/**
 * Registers the one service worker (public/sw.js) at scope "/". It covers the
 * launcher and every app under /c/, so push and notification clicks work for all
 * of them (docs/architecture.md §6, §7). Safe to call repeatedly.
 */
let registration: Promise<ServiceWorkerRegistration | null> | null = null;

export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return Promise.resolve(null);
  registration ??= navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err: unknown) => {
    console.warn('Circles: service worker registration failed', err);
    return null;
  });
  return registration;
}

/** Captured at load: the first worker claiming a fresh page is an install, not an update. */
const controlledAtLoad = typeof navigator !== 'undefined' && 'serviceWorker' in navigator && navigator.serviceWorker.controller !== null;

/**
 * Calls `listener` when a new sw.js is ready for a page that already had a worker: it
 * finished installing (its new app shell is cached; `installed`, waiting or not), it
 * activated, or it took control (`controllerchange`). sw.js calls skipWaiting +
 * clients.claim, so several arrive; lib/updates.ts treats them as one signal.
 */
export function onServiceWorkerUpdate(listener: () => void): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  let stopped = false;
  const fire = () => {
    if (!stopped) listener();
  };
  const onControllerChange = () => {
    if (controlledAtLoad) fire();
  };
  navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

  let reg: ServiceWorkerRegistration | null = null;
  const onUpdateFound = () => {
    const worker = reg?.installing;
    if (!worker || !reg?.active) return;
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' || worker.state === 'activated') fire();
    });
  };
  void registerServiceWorker().then((r) => {
    if (stopped || !r) return;
    reg = r;
    r.addEventListener('updatefound', onUpdateFound);
    if (r.waiting && r.active) fire();
  });

  return () => {
    stopped = true;
    navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    reg?.removeEventListener('updatefound', onUpdateFound);
  };
}

/**
 * Resolves once a new worker (installing or waiting) controls this page, asking a waiting
 * one to skip waiting; at once when there is none. A reload before that would get the
 * old worker's cached shell again.
 */
export function whenNewWorkerControls(reg: ServiceWorkerRegistration): Promise<void> {
  const worker = reg.waiting ?? reg.installing;
  if (!worker) return Promise.resolve();
  return new Promise((resolve) => {
    navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
    const check = () => {
      if (worker.state === 'installed') worker.postMessage({ type: 'SKIP_WAITING' });
      if (worker.state === 'activated' || worker.state === 'redundant') resolve();
    };
    worker.addEventListener('statechange', check);
    check();
  });
}
